import type { FormikHelpers } from 'formik';
import { Form, Formik } from 'formik';
import { navigate } from 'gatsby';
import React, { useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import type { Flags, Schema } from 'yup';
import { array, boolean, object, string } from 'yup';

import RecordAttachmentForm from '@components/Attachments/RecordAttachmentForm';
import { FormikFhirDateField, FormikTextField } from '@components/FormikFields';
import { RecordSubmitButton } from '@components/RecordSubmitButton';
import RelatedRecordsForm from '@components/RelatedRecordsTable/RelatedRecordsForm';

import { GENETIC_INFO_TYPES } from '@constants/option-types/genetic-info';
import * as ROUTES from '@constants/routes';

import {
  useCreateGeneticInformation,
  useGetGeneticInformationSingle,
  useUpdateGeneticInformation,
} from '@hooks/api/genetic-information';
import { useCuration } from '@hooks/useCuration';
import useFhirRecordForm from '@hooks/useFhirRecordForm';
import { useFormat } from '@hooks/useFormat';
import { useSession } from '@hooks/useSession';
import { useSnackbar } from '@hooks/useSnackbar';

import { HorizontalRule } from '@layouts/AuthLayout';
import { ColumnLayout, DrilldownItem } from '@layouts/DrilldownStyle';

import { getObservationTemplate } from '@models/fhir-templates/observation';
import type {
  FormValues,
  GeneticInfoValues,
  GeneticInformationObservationRecord,
} from '@models/genetic-information';

import { capitalize } from '@utils/format';
import { getRecordSourceForSubmit } from '@utils/getRecordSourceForSubmit';
import { stripEmptyStrings } from '@utils/strip';

import DrilldownWrapper from '../Common/DrilldownWrapper';

const messages = defineMessages({
  category: {
    id: 'geneticInfoDrilldown.category',
    defaultMessage: 'Category',
  },
  condition: {
    id: 'geneticInfoDrilldown.condition',
    defaultMessage: 'Genetic Condition',
  },
  date: {
    id: 'GLOBAL.date',
    defaultMessage: 'Date',
  },
  required: {
    id: 'GLOBAL.required',
    defaultMessage: 'This field is required',
  },
  error: {
    id: 'GLOBAL.error',
    defaultMessage: 'An error occurred. Please try again later.',
  },
});

interface GeneticInformationProps {
  id?: string;
  /**
   * When true, suppresses the default redirect to the GENETIC_INFORMATION route
   * after successfully creating a new genetic information record.
   * - Default: false (keeps existing behavior in normal routes)
   * - In auto-curation flow, this should be set to true.
   */
  disableNavigateOnCreate?: boolean;
  /**
   * Callback invoked after successful form submission.
   * Used by the curation flow to remove the form from the list.
   */
  onSubmit?: () => void;
}

const GeneticInformation: React.FC<GeneticInformationProps> = ({
  id = '',
  disableNavigateOnCreate = false,
  onSubmit,
}) => {
  const { formatMessage } = useIntl();
  const { formatDateLabel } = useFormat();
  const { enqueueSnackbar } = useSnackbar();
  const {
    formikHelpers: { baseFhirValidationSchema, initialFhirFormValues },
    loading: recordStatusAndSourceLoading,
    handleRecordFilesSourcePublishing,
  } = useFhirRecordForm();

  const {
    state: { activeProfile, isClient, isCurator },
  } = useSession();
  const {
    state: { isCreating, isEditing },
    finishAddingRecord,
    stopEditingRecord,
  } = useCuration();
  const [patient_id] = [activeProfile?.patient_id];

  const { data, loading: geneticInfoLoading } = useGetGeneticInformationSingle({
    record_id: id,
  });
  const [createGeneticInfo, createGeneticInfoLoading] =
    useCreateGeneticInformation();
  const [updateGeneticInfo, updateGeneticInfoLoading] =
    useUpdateGeneticInformation();

  const loading =
    geneticInfoLoading ||
    createGeneticInfoLoading ||
    updateGeneticInfoLoading ||
    recordStatusAndSourceLoading;

  useEffect(() => {
    if (!isCreating && isClient) {
      navigate(ROUTES.GENETIC_INFORMATION);
    }
  }, [isClient, isCreating]);

  const initialValues: FormValues = {
    ...initialFhirFormValues,
    title: data?.value ?? '',
    testDate: data?.testDate ?? '',
    code: data?.code ?? '',
    selfReported: data?.selfReported ?? false,
    associatedResources: [],
  };

  const geneticValidationSchema: Schema<Partial<GeneticInfoValues>> =
    object().shape({
      title: string().required(formatMessage(messages.required)),
      code: string().required(formatMessage(messages.required)),
      testDate: string(),
      selfReported: boolean().required(formatMessage(messages.required)),
      associatedResources: array().of(
        string().required(formatMessage(messages.required))
      ),
    });

  const validationSchema: Schema<
    Partial<GeneticInfoValues>,
    any,
    any,
    Flags
  > = geneticValidationSchema.concat(baseFhirValidationSchema);

  async function handleSubmit(
    values: FormValues,
    { validateForm }: FormikHelpers<FormValues>
  ) {
    if (!patient_id) return;
    values = stripEmptyStrings(values);
    await validateForm(stripEmptyStrings(values));

    let geneticInfo: GeneticInformationObservationRecord | null | undefined;
    try {
      if (data?.id) {
        const { data: updatedGenInfo } = await updateGeneticInfo({
          input: {
            id: data.id,
            observation: data.getModelInput(values),
          },
        });
        geneticInfo = updatedGenInfo;
      } else {
        const { data } = await createGeneticInfo({
          input: {
            ...getObservationTemplate(
              patient_id,
              values,
              'genetic-information'
            ),
          },
        });
        geneticInfo = data;
      }
    } catch {
      enqueueSnackbar({
        variant: 'error',
        message: formatMessage(messages.error),
      });
    } finally {
      await handleRecordFilesSourcePublishing({
        resource: geneticInfo?.getResourceId(),
        files: values.files,
        toPublish: values.toPublish,
        recordSource: getRecordSourceForSubmit({
          isCurator,
          isCreating,
          initialRecordSource: initialValues.recordSource,
          currentRecordSource: values.recordSource,
        }),
      });
      // Existing behavior preserved by default.
      // In auto-curation flow, disableNavigateOnCreate=true will skip this.
      if (geneticInfo && isCreating && !disableNavigateOnCreate) {
        navigate(`${ROUTES.GENETIC_INFORMATION}${geneticInfo.id}`);
      }
      stopEditingRecord();
      finishAddingRecord();
      onSubmit?.();
    }
  }

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      {({ setFieldValue, values }) => (
        <Form>
          <DrilldownWrapper
            associatedRecords={data?.associatedRecords}
            backRoute={ROUTES.GENETIC_INFORMATION}
            loading={loading}
            notes={data?.recordNotes}
            resource={data}
            selfReported={data?.selfReported}
            subtitle={
              isCreating || isEditing ? (
                <FormikFhirDateField
                  formats={['date', 'year']}
                  label={formatMessage(messages.date)}
                  name="testDate"
                  onChange={(value) => {
                    setFieldValue('testDate', value);
                  }}
                  value={data?.testDate}
                  max={true}
                />
              ) : (
                formatDateLabel(messages.date, data?.getTestDateStr())
              )
            }
            title={data?.value}
            titleInput={
              <FormikTextField
                name="title"
                placeholder={formatMessage(messages.condition)}
              />
            }
          >
            <ColumnLayout>
              <DrilldownItem
                label={formatMessage(messages.category)}
                name="code"
                onChange={(value) => setFieldValue('code', value)}
                options={GENETIC_INFO_TYPES}
                type="dropdown"
                value={capitalize(data?.code ?? '').replaceAll('-', ' ')}
              />
            </ColumnLayout>

            <RelatedRecordsForm associations={values.associatedResources} />

            <RecordAttachmentForm
              isCreating={isCreating}
              onFileChange={(files: DroppedFile[]) => {
                setFieldValue('files', files);
              }}
              patientId={patient_id}
              record={{
                attachments: data?.attachments,
                resourceId: data?.getResourceId(),
              }}
            />

            <HorizontalRule />
            <RecordSubmitButton />
          </DrilldownWrapper>
        </Form>
      )}
    </Formik>
  );
};

export default GeneticInformation;
